import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { OphirButton } from '@/components/common/OphirButton';
import { useContent } from '@/context/ContentContext';

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About' },
  { to: '/capabilities', label: 'Capabilities' },
  { to: '/categories', label: 'Product Categories' },
];

const SOLUTIONS_MENU = [
  { to: '/b2b-bulk-supplies', label: 'B2B Wholesale' },
  { to: '/institutional-buyers', label: 'Institutional Buyers' },
  { to: '/wholesale-distributor-supply', label: 'Wholesale Distribution' },
  { to: '/institutional-procurement', label: 'Product Sourcing & Procurement' },
  { to: '/request-bulk-quote', label: 'Request a Quote' },
];

export const Header = () => {
  const { content } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const announcement = content?.announcement;

  return (
    <header className="sticky top-0 z-50">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-[60] focus:bg-white focus:px-3 focus:py-2 focus:rounded focus:shadow">Skip to content</a>
      {announcement?.enabled && announcement?.text && (
        <div data-testid="announcement-bar" className="navy-gradient text-white">
          <div className="container-ophir py-2 text-center text-[12.5px] sm:text-[13px] tracking-wide text-white/90">
            {announcement.text}
          </div>
        </div>
      )}
      <div className={`bg-ophir-ivory-soft/95 backdrop-blur border-b border-ophir-grey transition-all duration-200 ${scrolled ? 'py-2' : 'py-3.5'}`}>
        <div className="container-ophir flex items-center justify-between gap-4">
          <Link to="/" data-testid="site-header-logo" className="flex items-center shrink-0" aria-label="OPHIR SUPPLY home">
            <img
              src="/images/ophir/logo/ophir-logo-full.png"
              alt="OPHIR SUPPLY — Wholesale, Distribution, Global Sourcing"
              className={`w-auto transition-all duration-200 ${scrolled ? 'h-9' : 'h-11'} md:${scrolled ? 'h-10' : 'h-12'}`}
              style={{ maxWidth: 240 }}
            />
          </Link>

          <nav data-testid="site-header-nav" className="hidden lg:flex items-center gap-6">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `relative text-[14.5px] font-medium py-1 transition-colors ${
                    isActive ? 'text-ophir-navy' : 'text-ophir-muted hover:text-ophir-navy'
                  } after:absolute after:left-0 after:-bottom-0.5 after:h-0.5 after:bg-ophir-gold after:transition-all after:duration-200 ${
                    isActive ? 'after:w-full' : 'after:w-0 hover:after:w-full'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {/* Solutions dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 text-[14.5px] font-medium py-1 text-ophir-muted hover:text-ophir-navy" data-testid="nav-solutions">
                Solutions <ChevronDown size={15} className="mt-0.5" />
              </button>
              <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity absolute right-0 top-full pt-3 z-50">
                <div className="w-64 rounded-xl border border-ophir-grey bg-white shadow-ophir-hover p-2">
                  {SOLUTIONS_MENU.map((s) => (
                    <Link key={s.to} to={s.to} className="block rounded-lg px-3 py-2.5 text-[14px] text-ophir-navy hover:bg-ophir-ivory">{s.label}</Link>
                  ))}
                </div>
              </div>
            </div>
            <NavLink to="/contact" className={({ isActive }) => `text-[14.5px] font-medium py-1 ${isActive ? 'text-ophir-navy' : 'text-ophir-muted hover:text-ophir-navy'}`}>Contact</NavLink>
          </nav>

          <div className="hidden lg:flex items-center gap-2.5">
            <OphirButton to="/request-bulk-quote" data-testid="header-rfq-cta" className="px-5 py-2.5 text-[14px]">
              Request Bulk Quote
            </OphirButton>
            <OphirButton variant="secondary" to="/suppliers" data-testid="header-primary-cta" className="px-4 py-2.5 text-[14px]">
              Become a Supplier
            </OphirButton>
          </div>

          <button
            data-testid="mobile-menu-open-button"
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg text-ophir-navy hover:bg-ophir-ivory"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ophir-navy-deep/40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-ophir-ivory-soft shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-ophir-grey">
              <img src="/images/ophir/logo/ophir-logo-full.png" alt="OPHIR SUPPLY" className="h-9 w-auto" style={{ maxWidth: 170 }} />
              <button data-testid="mobile-menu-close-button" onClick={() => setOpen(false)} className="h-11 w-11 inline-flex items-center justify-center rounded-lg text-ophir-navy hover:bg-ophir-ivory" aria-label="Close menu">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-3">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-3.5 text-[16px] font-medium ${
                      isActive ? 'bg-ophir-ivory text-ophir-navy' : 'text-ophir-text hover:bg-ophir-ivory'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <p className="px-4 pt-4 pb-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-ophir-gold-deep">Solutions</p>
              {SOLUTIONS_MENU.map((s) => (
                <NavLink key={s.to} to={s.to} className={({ isActive }) => `block rounded-lg px-4 py-3 text-[15px] ${isActive ? 'bg-ophir-ivory text-ophir-navy' : 'text-ophir-text hover:bg-ophir-ivory'}`}>{s.label}</NavLink>
              ))}
              <NavLink to="/suppliers" className={({ isActive }) => `block rounded-lg px-4 py-3.5 text-[16px] font-medium ${isActive ? 'bg-ophir-ivory text-ophir-navy' : 'text-ophir-text hover:bg-ophir-ivory'}`}>For Suppliers</NavLink>
              <NavLink to="/buyers" className={({ isActive }) => `block rounded-lg px-4 py-3.5 text-[16px] font-medium ${isActive ? 'bg-ophir-ivory text-ophir-navy' : 'text-ophir-text hover:bg-ophir-ivory'}`}>For Buyers</NavLink>
              <NavLink to="/contact" className={({ isActive }) => `block rounded-lg px-4 py-3.5 text-[16px] font-medium ${isActive ? 'bg-ophir-ivory text-ophir-navy' : 'text-ophir-text hover:bg-ophir-ivory'}`}>Contact</NavLink>
            </nav>
            <div className="border-t border-ophir-grey p-4 space-y-2.5">
              <OphirButton to="/request-bulk-quote" data-testid="mobile-rfq-cta" className="w-full">Request Bulk Quote</OphirButton>
              <OphirButton variant="secondary" to="/suppliers" className="w-full">Become a Supplier</OphirButton>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
